import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, TestBadge } from "@/components/common";
import { PlanInputsFields, NoCommercialPlan, usePlanInfo, buildInputsPayload } from "@/components/PlanInputsFields";
import { GuidedDocumentCreator } from "@/components/GuidedDocumentCreator";
import { HistoricalDocBuilder } from "@/components/HistoricalDocBuilder";
import { RecordObligationPaymentDialog, ObligationPaymentRows, openPaymentFor, canRecordAgainst, PURPOSES } from "@/components/ObligationPayment";
import { IndianRupee, FileText, CheckCircle2, PlusCircle, StickyNote, Pencil, Ban, Link2, Copy, ExternalLink } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;
const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
const humanize = (s) => (s || "").replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

const OBL_BADGE = {
  pending: "bg-muted text-muted-foreground", due: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  partial: "bg-sky-500/15 text-sky-600 dark:text-sky-400", paid: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  overdue: "bg-red-500/15 text-red-600 dark:text-red-400", na: "bg-muted text-muted-foreground",
};

// `onChanged` (optional, additive) — lets the host page refresh anything
// derived from payment state (e.g. Customer 360's live Process Next Action,
// which a verified payment can unblock) right after a verification.
export const FinanceTab = ({ contactId, opportunities = [], canRecord, canVerify, canDocs, canConfig, canHistoricalMigration, onChanged }) => {
  const [plans, setPlans] = useState(null);
  const [payOpen, setPayOpen] = useState(null); // obligation
  const [linkBusy, setLinkBusy] = useState(null); // obligation_id
  const [linkResult, setLinkResult] = useState(null); // {short_url, label}
  const [planOpen, setPlanOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null); // plan being edited
  const [editReason, setEditReason] = useState(""); // G-G — required when an obligation amount is overridden
  const [cancelOpen, setCancelOpen] = useState(null); // plan being cancelled
  const [cancelReason, setCancelReason] = useState("");
  const [checks, setChecks] = useState({});
  // Product-driven (2026-09 Commercial Integrity, Phase D) — New/Edit
  // Payment Plan use the SAME PlanInputsFields renderer + plan-info schema
  // as Finance -> Create & Calculate; no product-specific fields are
  // hard-coded here any more.
  const [np, setNp] = useState({ opportunity_id: "" });
  const [npInputs, setNpInputs] = useState({});
  const [npTotalAgreed, setNpTotalAgreed] = useState("");
  const [npDiscount, setNpDiscount] = useState("0");
  const npOpp = opportunities.find((o) => o.opportunity_id === np.opportunity_id);
  const { info: npInfo, error: npInfoError } = usePlanInfo(npOpp?.product_code);
  useEffect(() => { setNpInputs({}); setNpTotalAgreed(""); setNpDiscount("0"); }, [np.opportunity_id]);

  const [ep, setEp] = useState({ plan_id: "", total_agreed: "", discount: "0" });
  const [epInputs, setEpInputs] = useState({});
  const [epProductCode, setEpProductCode] = useState("");
  const { info: epInfo } = usePlanInfo(epProductCode);
  const [obligationEdits, setObligationEdits] = useState({}); // key -> {label, original, value}
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [adhoc, setAdhoc] = useState({ opportunity_id: "none", amount: "", description: "", category: "", obligation_id: "" });
  const [adhocBusy, setAdhocBusy] = useState(false);
  const offPlanDefaults = { amount: "", label: "", paid_at: new Date().toISOString().slice(0, 10), method: "upi", reference: "", notes: "" };
  const [offPlanOpen, setOffPlanOpen] = useState(null); // plan_id
  const [offPlan, setOffPlan] = useState(offPlanDefaults);
  const [manualDocs, setManualDocs] = useState([]);
  const prePlanDefaults = { opportunity_id: "none", classification: "registration", amount: "", label: "", description: "", gst_included: "true", gst_rate: "0.18", paid_at: new Date().toISOString().slice(0, 10), method: "upi", reference: "", notes: "" };
  const [prePlanOpen, setPrePlanOpen] = useState(false);
  const [prePlan, setPrePlan] = useState(prePlanDefaults);
  const [prePlanItems, setPrePlanItems] = useState([]); // unreconciled pre-plan obligations
  const [prePlanPayments, setPrePlanPayments] = useState([]); // their recorded/verified payments
  const [adjustOpen, setAdjustOpen] = useState(null); // pre-plan obligation being reconciled
  const [adjust, setAdjust] = useState({ plan_id: "", treatment: "adjust_first_instalment", target_obligation_key: "" });
  const [ledgers, setLedgers] = useState({}); // plan_id -> invoice_prep_ledger, fetched on demand

  const loadLedger = async (planId) => {
    if (ledgers[planId]) { setLedgers((prev) => { const n = { ...prev }; delete n[planId]; return n; }); return; }
    try {
      const { data } = await api.get(`/finance/plans/${planId}/invoice-prep`);
      setLedgers((prev) => ({ ...prev, [planId]: data }));
    } catch (e) { toast.error(errDetail(e)); }
  };

  const load = useCallback(() => {
    api.get(`/finance/plans/by-contact/${contactId}`).then(async (r) => {
      setPlans(r.data);
      const c = {};
      for (const s of r.data) {
        try { c[s.plan.plan_id] = (await api.get(`/finance/plans/${s.plan.plan_id}/document-checks`)).data; } catch { /* view-only */ }
      }
      setChecks(c);
    }).catch(() => setPlans([]));
    api.get(`/finance/documents/by-contact/${contactId}`).then((r) => setManualDocs(r.data)).catch(() => setManualDocs([]));
    api.get(`/finance/pre-plan/by-contact/${contactId}`).then((r) => {
      setPrePlanItems(r.data.obligations || []); setPrePlanPayments(r.data.payments || []);
    }).catch(() => { setPrePlanItems([]); setPrePlanPayments([]); });
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const createPlan = async () => {
    try {
      const { data: newPlan } = await api.post("/finance/plans", {
        opportunity_id: np.opportunity_id,
        total_agreed: npTotalAgreed ? Number(npTotalAgreed) : null,
        discount: Number(npDiscount || 0),
        inputs: buildInputsPayload(npInfo, npInputs),
      });
      toast.success("Payment plan created from the active Commercial Plan");
      setPlanOpen(false); load();
      // Prominently surface any Registration Fee / Advance payment already
      // collected pre-Signup for this exact opportunity — never silently
      // left unreconciled.
      const priorForThisOpp = prePlanItems.filter(
        (o) => o.opportunity_id === np.opportunity_id && ["registration", "advance"].includes(o.classification)
               && Number(o.amount_paid) > 0);
      if (priorForThisOpp.length && newPlan?.plan?.plan_id) {
        const first = priorForThisOpp[0];
        setAdjust({ plan_id: newPlan.plan.plan_id, treatment: "adjust_first_instalment",
                   target_obligation_key: (newPlan.obligations?.[0]?.key) || "" });
        setAdjustOpen(first);
      }
    } catch (e) { toast.error(errDetail(e)); }
  };

  const recordPrePlanPay = async () => {
    // "No specific opportunity yet" is a deliberate, supported choice —
    // money can be collected before any product is confirmed — so it's
    // never rejected here, only validated for the fields that actually
    // matter (amount, and a label when it's an add-on).
    if (prePlan.classification === "addon" && !prePlan.label.trim()) {
      toast.error("Describe what this add-on/service is for"); return;
    }
    try {
      const { data } = await api.post("/finance/payments/pre-plan", {
        contact_id: contactId,
        opportunity_id: prePlan.opportunity_id === "none" ? null : prePlan.opportunity_id,
        classification: prePlan.classification, amount: Number(prePlan.amount),
        label: prePlan.label || null, description: prePlan.description || null,
        gst_included: prePlan.classification === "addon" ? prePlan.gst_included === "true" : null,
        gst_rate: prePlan.classification === "addon" ? Number(prePlan.gst_rate) : null,
        paid_at: `${prePlan.paid_at}T12:00:00Z`, method: prePlan.method,
        reference: prePlan.reference || null, notes: prePlan.notes || null,
      });
      const cos = data?.consultation_outcome_status;
      if (prePlan.classification === "advance" && cos && !cos.completed) {
        // The payment is safely recorded either way — this is a real
        // requirement, not a generic override, so it stays a persistent
        // banner on the Pre-Signup payments card (below) rather than a
        // one-off toast that can be missed.
        toast.warning("Payment recorded. This opportunity's Consultation Outcome must be "
          + "completed before it can move to Signup — log it from the Calls/Consultations tab.",
          { duration: 8000 });
      } else {
        toast.success("Payment recorded — awaiting verification");
      }
      setPrePlanOpen(false); setPrePlan(prePlanDefaults); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const applyAdjustment = async () => {
    if (adjust.treatment === "adjust_first_instalment" && !adjust.target_obligation_key) {
      toast.error("Pick which instalment this payment should be adjusted against"); return;
    }
    try {
      await api.post(`/finance/plans/${adjust.plan_id}/registration-adjustment`, {
        plan_id: adjust.plan_id, pre_plan_obligation_id: adjustOpen.obligation_id,
        treatment: adjust.treatment,
        target_obligation_key: adjust.treatment === "adjust_first_instalment" ? adjust.target_obligation_key : null,
      });
      toast.success("Reconciled against the Payment Plan");
      setAdjustOpen(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const openEdit = (s) => {
    setEp({
      plan_id: s.plan.plan_id,
      total_agreed: "", // override field starts blank — blank means "use the calculated default"
      discount: s.plan.discount ?? "0",
    });
    // Pre-fill with this plan's OWN previously-saved inputs — whatever
    // keys its product's Commercial Plan actually uses (buildInputsPayload
    // filters to those on save, so a stray key here, e.g. a legacy
    // `reimbursements` snapshot, is harmless).
    setEpInputs(s.plan.inputs || {});
    setEpProductCode(s.plan.product_code);
    const oblMap = {};
    for (const o of s.obligations) {
      // Any obligation not yet paid is overridable — not a fixed key list,
      // so this works for arbitrary v2 instalments/fee items too.
      if (o.status !== "paid") {
        oblMap[o.key] = { label: o.label, original: o.expected_amount, value: o.expected_amount };
      }
    }
    setObligationEdits(oblMap);
    setEditReason("");
    setEditOpen(s.plan.plan_id);
  };

  const saveEdit = async () => {
    const overrides = {};
    for (const [key, o] of Object.entries(obligationEdits)) {
      if (Number(o.value) !== Number(o.original)) overrides[key] = Number(o.value);
    }
    // G-G — an obligation-amount override is a genuine monetary correction,
    // not a presentation edit; require a stated reason so calculated vs
    // revised value, reason, user and timestamp all land in the audit trail.
    if (Object.keys(overrides).length > 0 && !editReason.trim()) {
      toast.error("A reason is required when overriding a calculated instalment amount");
      return;
    }
    try {
      await api.patch(`/finance/plans/${ep.plan_id}`, {
        total_agreed: ep.total_agreed ? Number(ep.total_agreed) : null,
        discount: ep.discount ? Number(ep.discount) : 0,
        inputs: buildInputsPayload(epInfo, epInputs),
        obligation_overrides: Object.keys(overrides).length ? overrides : null,
        reason: Object.keys(overrides).length ? editReason.trim() : null,
      });
      toast.success("Payment plan updated — obligations recalculated");
      setEditOpen(null); setEditReason(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const cancelPlan = async () => {
    try {
      await api.post(`/finance/plans/${cancelOpen}/discontinue`, { reason: cancelReason });
      toast.success("Payment plan cancelled");
      setCancelOpen(null); setCancelReason(""); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const recordOffPlanPay = async () => {
    if (!offPlan.label.trim()) { toast.error("Describe what this payment is for"); return; }
    try {
      await api.post("/finance/payments/off-plan", {
        plan_id: offPlanOpen, amount: Number(offPlan.amount), label: offPlan.label.trim(),
        paid_at: `${offPlan.paid_at}T12:00:00Z`, method: offPlan.method,
        reference: offPlan.reference || null, notes: offPlan.notes || null,
      });
      toast.success("Additional payment recorded — awaiting verification");
      setOffPlanOpen(null); setOffPlan(offPlanDefaults); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const generateLink = async (o) => {
    setLinkBusy(o.obligation_id);
    try {
      const { data } = await api.post("/finance/payment-links", { obligation_id: o.obligation_id });
      setLinkResult({ short_url: data.short_url, label: o.label });
    } catch (e) { toast.error(errDetail(e)); } finally { setLinkBusy(null); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(linkResult.short_url); toast.success("Link copied"); }
    catch { toast.error("Could not copy — select and copy manually"); }
  };

  // The plan (if any) behind the opportunity currently picked in the ad-hoc
  // dialog — used to offer an installment picker and detect a full settlement.
  const adhocPlan = (plans || []).find((s) => s.plan.opportunity_id === adhoc.opportunity_id);
  const adhocOutstandingObls = adhocPlan ? adhocPlan.obligations.filter((o) => o.status !== "paid" && o.status !== "na") : [];
  const adhocIsFullSettlement = adhocPlan && adhoc.amount
    && Math.abs(Number(adhocPlan.totals.total_paid) + Number(adhoc.amount) - Number(adhocPlan.totals.total_agreed)) < 0.01;

  const generateAdhocLink = async () => {
    setAdhocBusy(true);
    try {
      const { data } = await api.post("/finance/payment-links/adhoc", {
        contact_id: contactId,
        opportunity_id: adhoc.opportunity_id === "none" ? null : adhoc.opportunity_id,
        amount: adhoc.amount,
        description: adhoc.description,
        category: adhoc.category,
        obligation_id: adhoc.category === "installment" && !adhocIsFullSettlement ? adhoc.obligation_id : null,
      });
      setLinkResult({ short_url: data.short_url, label: adhoc.description });
      setAdhocOpen(false);
      setAdhoc({ opportunity_id: "none", amount: "", description: "", category: "", obligation_id: "" });
    } catch (e) { toast.error(errDetail(e)); } finally { setAdhocBusy(false); }
  };

  const verify = async (pid) => {
    try { await api.post(`/finance/payments/${pid}/verify`, { approve: true }); toast.success("Payment verified"); load(); onChanged?.(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const milestone = async (planId, ms) => {
    try { const r = await api.post(`/finance/plans/${planId}/milestone`, { milestone: ms }); toast.success(r.data.note || `Milestone recorded${r.data.third_activated ? " — third instalment activated" : ""}`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const genDoc = async (planId, docType) => {
    try { const r = await api.post(`/finance/plans/${planId}/generate-document`, { doc_type: docType }); toast.success(`${r.data.doc_number} generated, stored and queued for delivery`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  if (plans === null) return <div className="p-6 text-sm text-muted-foreground">Loading finance…</div>;
  // Surface "ready for final tax invoice" the moment this customer's Finance
  // tab opens, rather than staff needing to find the right plan card and
  // scroll to its Documents row to discover it.
  const readyForFinalInvoice = plans.filter((s) => {
    const c = (checks[s.plan.plan_id] || {}).final_invoice;
    return c && c.eligible && !c.already_issued;
  });
  return (
    <div className="space-y-4" data-testid="c360-finance">
      {canRecord && (
        <div className="flex flex-wrap justify-end gap-2" data-testid="finance-header-actions">
          <Button size="sm" variant="outline" data-testid="finance-record-pre-plan-btn" onClick={() => { setPrePlan(prePlanDefaults); setPrePlanOpen(true); }}>
            <IndianRupee className="h-4 w-4 mr-1" />Record payment (pre-Signup)</Button>
          <Button size="sm" variant="outline" data-testid="finance-new-adhoc-link-btn" onClick={() => setAdhocOpen(true)}><Link2 className="h-4 w-4 mr-1" />New payment link</Button>
          <Button size="sm" data-testid="finance-new-plan-btn" onClick={() => setPlanOpen(true)}><PlusCircle className="h-4 w-4 mr-1" />New payment plan</Button>
        </div>
      )}

      {prePlanItems.length > 0 && (
        <Card data-testid="c360-pre-plan-payments">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Pre-Signup payments (not yet on a Payment Plan)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {prePlanItems.map((o) => {
              const matchingPlan = plans.find((s) => s.plan.opportunity_id === o.opportunity_id);
              const needsOutcome = o.classification === "advance" && o.consultation_outcome_status
                && !o.consultation_outcome_status.completed;
              return (
                <div key={o.obligation_id} className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm" data-testid={`pre-plan-item-${o.obligation_id}`}>
                    <Badge variant="outline" className="capitalize">{o.classification}</Badge>
                    {PURPOSES[o.purpose] && <Badge variant="outline" data-testid={`pre-plan-purpose-${o.obligation_id}`}>{PURPOSES[o.purpose].label}</Badge>}
                    <span className="font-medium">{o.label}</span>
                    <span className="text-xs text-muted-foreground">Paid: {inr(o.amount_paid)} of {inr(o.expected_amount)}</span>
                    <Badge className={OBL_BADGE[o.status] || ""}>{o.status}</Badge>
                    {o.adjustment === "needs_resolution" && (
                      <Badge className="bg-red-500/15 text-red-600" data-testid={`needs-resolution-${o.obligation_id}`}>
                        Needs resolution — outcome was not Signup</Badge>
                    )}
                    {o.classification !== "addon" && Number(o.amount_paid) > 0 && !needsOutcome && (
                      matchingPlan ? (
                        <Button size="sm" variant="outline" className="ml-auto" data-testid={`reconcile-${o.obligation_id}`}
                          onClick={() => { setAdjust({ plan_id: matchingPlan.plan.plan_id, treatment: "adjust_first_instalment", target_obligation_key: matchingPlan.obligations?.[0]?.key || "" }); setAdjustOpen(o); }}>
                          Reconcile against Payment Plan
                        </Button>
                      ) : (
                        <span className="ml-auto text-xs text-muted-foreground">No Payment Plan yet — reconcile once one is created</span>
                      )
                    )}
                    {/* R5 (gap G1) — record an already-received payment
                        against THIS existing pre-plan obligation (e.g. the
                        Partnership IELTS/PTE class fee a Case raised),
                        instead of creating a second obligation. */}
                    {canRecordAgainst(o, prePlanPayments, canRecord) && (
                      <Button size="sm" variant="outline" className="ml-auto" data-testid={`pre-plan-record-${o.obligation_id}`}
                        onClick={() => setPayOpen(openPaymentFor(o))}>Record payment</Button>
                    )}
                  </div>
                  {PURPOSES[o.purpose] && o.status !== "paid" && (
                    <div className="text-xs text-muted-foreground pl-2">{PURPOSES[o.purpose].unlocks}</div>
                  )}
                  <div className="pl-2 space-y-1">
                    <ObligationPaymentRows obligation={o} payments={prePlanPayments} canVerify={canVerify} onVerify={verify} />
                  </div>
                  {needsOutcome && (
                    <div className="text-xs text-amber-600 dark:text-amber-400 pl-2" data-testid={`needs-outcome-${o.obligation_id}`}>
                      ⚠ Consultation Outcome required for this opportunity before it can move to Signup — log it from the Calls/Consultations tab.
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      {canDocs && readyForFinalInvoice.length > 0 && (
        <Card className="border-[hsl(var(--success))]/40" data-testid="c360-final-invoice-ready">
          <CardContent className="pt-4 flex items-center gap-3 flex-wrap">
            <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Ready for final tax invoice</p>
              <p className="text-xs text-muted-foreground">{readyForFinalInvoice.map((s) => s.plan.product_code).join(", ")} — every obligation is paid and no final invoice has been issued yet.</p>
            </div>
            {readyForFinalInvoice.map((s) => (
              <GuidedDocumentCreator key={s.plan.plan_id} contactId={contactId}
                opportunities={[{ opportunity_id: s.plan.opportunity_id, product_code: s.plan.product_code, product_name: s.plan.product_code }]}
                presetOpportunityId={s.plan.opportunity_id} forceMode="final_invoice"
                triggerLabel={`Generate final tax invoice — ${s.plan.product_code}`}
                data-testid={`gen-final-invoice-ready-${s.plan.plan_id}`} onGenerated={load} />
            ))}
          </CardContent>
        </Card>
      )}
      {plans.length === 0 && <EmptyState icon={IndianRupee} title="No payment plan yet" hint="Create a payment plan from the active Commercial Plan to start tracking instalments." />}
      {plans.map((s) => {
        const t = s.totals || {}; const ck = checks[s.plan.plan_id] || {};
        return (
          <Card key={s.plan.plan_id} data-testid={`finance-plan-${s.plan.plan_id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex flex-wrap items-center gap-2">
                <IndianRupee className="h-4 w-4 text-primary" />
                {s.plan.product_code} — Commercial Plan v{s.plan.commercial_plan_version}
                {s.plan.is_test_record && <TestBadge show />}
                {s.plan.npa && <Badge variant="destructive">NPA</Badge>}
                {s.plan.discontinued && <Badge variant="destructive">Discontinued</Badge>}
                {canConfig && !s.plan.discontinued && (
                  <span className="ml-auto flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" data-testid={`edit-plan-${s.plan.plan_id}`} onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit plan</Button>
                    <Button size="sm" variant="outline" className="text-destructive" data-testid={`cancel-plan-${s.plan.plan_id}`} onClick={() => setCancelOpen(s.plan.plan_id)}><Ban className="h-3.5 w-3.5 mr-1" />Cancel plan</Button>
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                {[["Total agreed", t.total_agreed], ["Total paid", t.total_paid], ["Outstanding", t.outstanding], ["Overdue", t.overdue]].map(([l, v]) => (
                  <div key={l} className="rounded-md border p-2"><div className="text-xs text-muted-foreground">{l}</div><div className="font-semibold" data-testid={`finance-${l.toLowerCase().replace(" ", "-")}`}>{inr(v)}</div></div>
                ))}
                <div className="rounded-md border p-2"><div className="text-xs text-muted-foreground">Effective booking date</div><div className="font-medium text-xs">{s.plan.effective_booking_date ? fmtIST(s.plan.effective_booking_date) : "— (pending full booking payment)"}</div></div>
              </div>
              {/* Payment Reallocation (2026-09) — surfaced identically on
                  both sides: a non-cash internal transfer, never counted
                  in Total paid above, which stays cash-only. */}
              {(s.reallocations_in?.length > 0 || s.reallocations_out?.length > 0) && (
                <div className="rounded-md border border-sky-300 bg-sky-500/10 p-2 text-xs space-y-1"
                    data-testid={`reallocations-${s.plan.plan_id}`}>
                  {s.reallocations_in?.map((r) => (
                    <div key={r.reallocation_id}>
                      ↓ Received {inr(r.amount)} reallocated from another opportunity's payment ({fmtIST(r.decided_at)})
                    </div>
                  ))}
                  {s.reallocations_out?.map((r) => (
                    <div key={r.reallocation_id}>
                      ↑ {inr(r.amount)} of this plan's payment was reallocated to another opportunity ({fmtIST(r.decided_at)})
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Instalments</div>
                <Table>
                  <TableHeader><TableRow><TableHead>Obligation</TableHead><TableHead>Expected</TableHead><TableHead>Paid</TableHead><TableHead>Outstanding</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                  <TableBody>
                    {s.obligations.map((o) => (
                      <TableRow key={o.obligation_id} data-testid={`obligation-${o.key}`}>
                        <TableCell className="font-medium">{o.label}</TableCell>
                        <TableCell>{inr(o.expected_amount)}</TableCell>
                        <TableCell>{inr(o.amount_paid)}</TableCell>
                        <TableCell>{inr(o.outstanding)}</TableCell>
                        <TableCell className="text-xs">{o.due_at ? fmtIST(o.due_at) : o.activation?.startsWith("milestone") ? `on ${o.trigger?.replaceAll("_", " ")}` : "—"}</TableCell>
                        <TableCell><Badge className={OBL_BADGE[o.status] || ""}>{o.status}</Badge></TableCell>
                        <TableCell>{canRecord && ["due", "partial", "overdue"].includes(o.status) && (
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" data-testid={`record-payment-${o.key}`} onClick={() => setPayOpen(openPaymentFor({ ...o, plan_id: s.plan.plan_id }))}>Record payment</Button>
                            <Button size="sm" variant="outline" data-testid={`payment-link-${o.key}`} disabled={linkBusy === o.obligation_id} onClick={() => generateLink(o)}>
                              <Link2 className="h-3.5 w-3.5 mr-1" />{linkBusy === o.obligation_id ? "Generating…" : "Payment link"}
                            </Button>
                          </div>)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {s.payments.length > 0 && (() => {
                // Grouped by obligation, never collapsed — every individual
                // part-payment stays visible, nested under the obligation
                // it belongs to with a running subtotal, so invoice prep
                // sees exactly which payments make up an obligation's
                // total at a glance. Same flat payments/obligations data
                // plan_summary already returns — grouped client-side only.
                const labelByKey = Object.fromEntries(s.obligations.map((o) => [o.key, o.label]));
                const groups = {};
                for (const p of s.payments) (groups[p.obligation_key] ||= []).push(p);
                return (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">Payment history (immutable — partials preserved)</div>
                    <div className="space-y-2">
                      {Object.entries(groups).map(([key, ps]) => {
                        const verifiedTotal = ps.filter((p) => p.verification_status === "verified")
                          .reduce((sum, p) => sum + Number(p.amount), 0);
                        return (
                          <div key={key} className="rounded-md border p-2" data-testid={`payment-group-${key}`}>
                            <div className="flex items-center justify-between text-xs font-medium mb-1">
                              <span>{labelByKey[key] || key}{key.startsWith("offplan:") && <Badge variant="outline" className="ml-1.5 align-middle">Additional / off-plan</Badge>}</span>
                              <span className="text-muted-foreground">{ps.length} payment{ps.length > 1 ? "s" : ""} · {inr(verifiedTotal)} verified</span>
                            </div>
                            <div className="space-y-1">
                              {ps.map((p) => (
                                <div key={p.payment_id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-1.5 text-xs" data-testid={`payment-${p.payment_id}`}>
                                  <span className="font-semibold">{inr(p.amount)}</span>
                                  <span className="text-muted-foreground">{fmtIST(p.paid_at)} · {p.method} · {p.reference || "no ref"}</span>
                                  <Badge className={p.verification_status === "verified" ? OBL_BADGE.paid : OBL_BADGE.due}>{p.verification_status}</Badge>
                                  {canVerify && p.verification_status === "recorded" && (
                                    <Button size="sm" variant="ghost" className="h-6 px-2" data-testid={`verify-${p.payment_id}`} onClick={() => verify(p.payment_id)}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Verify</Button>)}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <div className="flex flex-wrap gap-2">
                {canVerify && (<>
                  {s.obligations.filter((o) => o.status === "pending" && o.trigger).map((o) => (
                    <Button key={o.key} size="sm" variant="outline" data-testid={`milestone-${o.trigger}`}
                      onClick={() => milestone(s.plan.plan_id, o.trigger)}>{humanize(o.trigger)}</Button>
                  ))}
                  <Button size="sm" variant="outline" data-testid="milestone-visa-negative" onClick={() => milestone(s.plan.plan_id, "visa_outcome_negative")}>Visa outcome: negative</Button>
                </>)}
                {canRecord && !s.plan.discontinued && (
                  <Button size="sm" variant="outline" data-testid={`off-plan-payment-${s.plan.plan_id}`}
                    onClick={() => { setOffPlan({ ...offPlanDefaults }); setOffPlanOpen(s.plan.plan_id); }}>
                    <PlusCircle className="h-3.5 w-3.5 mr-1" />Additional payment</Button>
                )}
                <Button size="sm" variant="outline" data-testid={`invoice-prep-toggle-${s.plan.plan_id}`}
                  onClick={() => loadLedger(s.plan.plan_id)}>
                  <FileText className="h-3.5 w-3.5 mr-1" />
                  {ledgers[s.plan.plan_id] ? "Hide" : "Complete payment picture"}
                </Button>
              </div>
              {ledgers[s.plan.plan_id] && (() => {
                const led = ledgers[s.plan.plan_id];
                const sm = led.summary;
                return (
                  <div className="rounded-md border p-3 space-y-2" data-testid={`invoice-prep-${s.plan.plan_id}`}>
                    <div className="text-xs font-medium">Complete payment picture — everything relevant to this plan, on one screen</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      {[["Commercial Plan value", sm.commercial_plan_value], ["Core paid", sm.core_paid],
                        ["Registration paid", sm.registration_paid], ["Advance paid", sm.advance_paid],
                        ["Add-ons paid", sm.addons_paid], ["Off-plan paid", sm.offplan_paid],
                        ["Total received", sm.total_received], ["Outstanding balance", sm.outstanding_commercial_plan_balance]].map(([l, v]) => (
                        <div key={l} className="rounded-md border p-2"><div className="text-muted-foreground">{l}</div><div className="font-semibold">{inr(v)}</div></div>
                      ))}
                    </div>
                    {[["Registration", led.registration], ["Advance", led.advance], ["Add-ons", led.addons], ["Off-plan / other", led.offplan]]
                      .filter(([, group]) => group.length > 0).map(([label, group]) => (
                        <div key={label} className="text-xs">
                          <div className="font-medium text-muted-foreground mb-0.5">{label}</div>
                          {group.map((o) => (
                            <div key={o.obligation_id} className="flex items-center gap-x-2 gap-y-0.5 flex-wrap pl-2">
                              <span>{o.label}</span>
                              <span className="text-muted-foreground">Paid: {inr(o.amount_paid)}{o.adjustment && o.adjustment !== "unresolved" && ` · Adjustment: ${o.adjustment.replaceAll("_", " ")}`}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                );
              })()}
              {canDocs && (
                <div className="rounded-md border p-3 space-y-2">
                  <div className="text-xs font-medium flex items-center gap-1"><FileText className="h-3.5 w-3.5" />Documents (generated only after verified payment)</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(ck).map(([dt, c]) => {
                      // G-F — the Final/Discontinued Tax Invoice always goes
                      // through Draft/Preview -> Review -> Finalize, even
                      // from this fixed-button row; booking_receipt/
                      // second_receipt (pre-existing, unchanged) stay
                      // one-click, matching G1's "preserve existing".
                      if (dt === "final_invoice" || dt === "discontinued_invoice") {
                        if (c.already_issued) {
                          return <Button key={dt} size="sm" variant="outline" disabled data-testid={`gen-${dt}`}>{dt.replaceAll("_", " ")} ✓ {c.already_issued}</Button>;
                        }
                        return (
                          <GuidedDocumentCreator key={dt} contactId={contactId} opportunities={[{
                            opportunity_id: s.plan.opportunity_id, product_code: s.plan.product_code,
                            product_name: s.plan.product_code }]}
                            presetOpportunityId={s.plan.opportunity_id} forceMode="final_invoice"
                            triggerLabel={dt.replaceAll("_", " ")} variant={c.eligible ? "default" : "outline"}
                            data-testid={`gen-${dt}`} onGenerated={load} />
                        );
                      }
                      return (
                        <div key={dt} className="flex items-center gap-1">
                          <Button size="sm" variant={c.eligible ? "default" : "outline"} disabled={!c.eligible || !!c.already_issued} data-testid={`gen-${dt}`}
                            title={c.already_issued ? `Issued: ${c.already_issued}` : c.reason} onClick={() => genDoc(s.plan.plan_id, dt)}>
                            {dt.replaceAll("_", " ")}{c.already_issued ? ` ✓ ${c.already_issued}` : ""}
                          </Button>
                        </div>
                      );
                    })}
                    {/* Phase G13 — the guided creator, preselected to this
                        opportunity/plan since the context is already known;
                        covers any individual instalment/add-on payment the
                        four fixed buttons above don't (they're scoped to
                        booking/second/final/discontinued only). */}
                    <GuidedDocumentCreator contactId={contactId} opportunities={[{
                      opportunity_id: s.plan.opportunity_id, product_code: s.plan.product_code,
                      product_name: s.plan.product_code }]}
                      presetOpportunityId={s.plan.opportunity_id} triggerLabel="Generate receipt / invoice…"
                      variant="outline" onGenerated={load} />
                  </div>
                  {Object.values(ck).some((c) => !c.eligible && c.reason && !c.already_issued) && (
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {Object.entries(ck).filter(([, c]) => !c.eligible && !c.already_issued && c.reason).map(([dt, c]) => (
                        <div key={dt} data-testid={`gate-${dt}`}>• {dt.replaceAll("_", " ")}: {c.reason}</div>))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {manualDocs.length > 0 && (
        <Card data-testid="c360-manual-docs">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Ad-hoc & manual payments</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {manualDocs.map((d) => (
              <div key={d.document_id} className="flex items-center gap-2 p-2 rounded-md border text-sm flex-wrap" data-testid={`manual-doc-${d.doc_number}`}>
                <span className="font-medium">{d.doc_number}</span>
                <TestBadge show={d.is_test_record} />
                <span className="text-xs text-muted-foreground">{d.doc_date} · {(d.doc_type || "").replaceAll("_", " ")}</span>
                <span className="text-xs text-muted-foreground truncate max-w-60">{(d.lines || []).map((l) => l.label).join(", ")}</span>
                <span className="ml-auto font-semibold">{inr(d.summary?.total)}</span>
                <a href={`${backend}/api/finance/documents/${d.document_id}/pdf`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="ghost" data-testid={`manual-doc-pdf-${d.doc_number}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                </a>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Phase G8/G13 — same shared builder Finance's own page uses, never a
          second implementation. Historical migration record type only
          offered to a user holding finance.historical_migration. */}
      {canDocs && (
        <HistoricalDocBuilder contact={{ contact_id: contactId }} opps={opportunities}
          onCreated={load} canHistoricalMigration={canHistoricalMigration} />
      )}

      <Dialog open={adhocOpen} onOpenChange={setAdhocOpen}>
        <DialogContent data-testid="adhoc-payment-link-dialog">
          <DialogHeader><DialogTitle>New payment link</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">For a one-off amount collected outside the normal instalment flow. Once Razorpay confirms payment, a receipt is generated and sent automatically.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Description (becomes the receipt line item)</Label>
              <Input data-testid="adhoc-description" placeholder="e.g. Document courier fee" value={adhoc.description} onChange={(e) => setAdhoc({ ...adhoc, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount (₹)</Label>
              <Input data-testid="adhoc-amount" type="number" min="0" step="0.01" value={adhoc.amount} onChange={(e) => setAdhoc({ ...adhoc, amount: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">What is this payment for?</Label>
              <Select value={adhoc.category} onValueChange={(v) => setAdhoc({ ...adhoc, category: v, obligation_id: "" })}>
                <SelectTrigger data-testid="adhoc-category"><SelectValue placeholder="Choose one…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="installment">Part-payment toward an installment</SelectItem>
                  <SelectItem value="addon">Add-on / extra service (not part of the package)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Installment payments count toward that instalment's balance and the customer's final tax invoice. Add-ons stay a separate receipt — real income, but not part of the package price.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Opportunity {adhoc.category === "installment" ? "(required)" : "(optional)"}</Label>
              <Select value={adhoc.opportunity_id} onValueChange={(v) => setAdhoc({ ...adhoc, opportunity_id: v, obligation_id: "" })}>
                <SelectTrigger data-testid="adhoc-opportunity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not linked</SelectItem>
                  {opportunities.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name || o.product_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {adhoc.category === "installment" && adhoc.opportunity_id !== "none" && !adhocPlan && (
              <p className="text-xs text-[hsl(var(--warning))]">This opportunity has no payment plan yet — create one first, or tag this as an add-on instead.</p>
            )}
            {adhoc.category === "installment" && adhocPlan && (
              adhocIsFullSettlement ? (
                <p className="text-xs text-[hsl(var(--success))]" data-testid="adhoc-full-settlement-note">This amount closes out the full package — every remaining instalment will be marked paid, no need to pick one.</p>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs">Which instalment?</Label>
                  <Select value={adhoc.obligation_id} onValueChange={(v) => setAdhoc({ ...adhoc, obligation_id: v })}>
                    <SelectTrigger data-testid="adhoc-obligation"><SelectValue placeholder="Choose the instalment…" /></SelectTrigger>
                    <SelectContent>
                      {adhocOutstandingObls.map((o) => <SelectItem key={o.obligation_id} value={o.obligation_id}>{o.label} — outstanding {inr(o.outstanding)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {adhocOutstandingObls.length === 0 && <p className="text-[11px] text-muted-foreground">No outstanding instalments on this plan.</p>}
                </div>
              )
            )}
          </div>
          <DialogFooter>
            <Button data-testid="adhoc-generate" disabled={adhocBusy || !adhoc.description.trim() || !(Number(adhoc.amount) > 0) || !adhoc.category
              || (adhoc.category === "installment" && (adhoc.opportunity_id === "none" || !adhocPlan || (!adhocIsFullSettlement && !adhoc.obligation_id)))}
              onClick={generateAdhocLink}>
              {adhocBusy ? "Generating…" : "Generate link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkResult} onOpenChange={(o) => !o && setLinkResult(null)}>
        <DialogContent data-testid="payment-link-dialog">
          <DialogHeader><DialogTitle>Payment link — {linkResult?.label}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Share this with the customer — WhatsApp, SMS or email. It's built to collect exactly this instalment; once Razorpay confirms payment, it's recorded and verified here automatically.</p>
            <div className="flex items-center gap-2">
              <code data-testid="payment-link-url" className="flex-1 font-mono text-xs bg-muted px-2 py-1.5 rounded-md truncate">{linkResult?.short_url}</code>
              <Button size="sm" variant="outline" data-testid="payment-link-copy" onClick={copyLink}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLinkResult(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <RecordObligationPaymentDialog obligation={payOpen} onClose={() => setPayOpen(null)} onRecorded={load} />

      <Dialog open={!!offPlanOpen} onOpenChange={(o) => !o && setOffPlanOpen(null)}>
        <DialogContent data-testid="off-plan-payment-dialog">
          <DialogHeader><DialogTitle>Additional payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">For a payment that doesn't match any instalment above — e.g. a one-off charge, a correction, or something outside the Commercial Plan. It's recorded as its own clearly-labeled line, never guessed onto an existing instalment.</div>
            <div><Label>What is this payment for?</Label><Input data-testid="off-plan-label" value={offPlan.label} onChange={(e) => setOffPlan({ ...offPlan, label: e.target.value })} placeholder="e.g. Courier charges reimbursement" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount received</Label><Input data-testid="off-plan-amount" type="number" value={offPlan.amount} onChange={(e) => setOffPlan({ ...offPlan, amount: e.target.value })} /></div>
              <div><Label>Payment date</Label><Input data-testid="off-plan-date" type="date" value={offPlan.paid_at} onChange={(e) => setOffPlan({ ...offPlan, paid_at: e.target.value })} /></div>
              <div><Label>Method</Label>
                <Select value={offPlan.method} onValueChange={(v) => setOffPlan({ ...offPlan, method: v })}>
                  <SelectTrigger data-testid="off-plan-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["upi", "bank_transfer", "card", "cash", "cheque", "gateway"].map((x) => <SelectItem key={x} value={x}>{x.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Transaction reference</Label><Input data-testid="off-plan-reference" value={offPlan.reference} onChange={(e) => setOffPlan({ ...offPlan, reference: e.target.value })} placeholder="UTR / txn id" /></div>
            </div>
            <div><Label>Notes</Label><Textarea data-testid="off-plan-notes" value={offPlan.notes} onChange={(e) => setOffPlan({ ...offPlan, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="off-plan-submit" onClick={recordOffPlanPay} disabled={!offPlan.amount || !offPlan.label.trim()}>Record payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={prePlanOpen} onOpenChange={setPrePlanOpen}>
        <DialogContent data-testid="pre-plan-payment-dialog">
          <DialogHeader><DialogTitle>Record a pre-Signup payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">For money already received before this customer has a Payment Plan — no plan, instalment, or Signup stage required. What is this payment for?</p>
            <div><Label>Opportunity (optional — a customer can have money collected before any specific product is confirmed)</Label>
              <Select value={prePlan.opportunity_id} onValueChange={(v) => setPrePlan({ ...prePlan, opportunity_id: v })}>
                <SelectTrigger data-testid="pre-plan-opportunity"><SelectValue placeholder="Select opportunity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific opportunity yet</SelectItem>
                  {opportunities.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_code} · {o.opportunity_id.slice(0, 8)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>What is this payment for?</Label>
              <Select value={prePlan.classification} onValueChange={(v) => setPrePlan({ ...prePlan, classification: v })}>
                <SelectTrigger data-testid="pre-plan-classification"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="registration">Registration Fee</SelectItem>
                  <SelectItem value="advance">Advance / Part payment towards service package</SelectItem>
                  <SelectItem value="addon">Add-on / Additional service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {prePlan.classification === "addon" && (
              <div><Label>Add-on / service label</Label><Input data-testid="pre-plan-label" value={prePlan.label} onChange={(e) => setPrePlan({ ...prePlan, label: e.target.value })} placeholder="e.g. SOP Service" /></div>
            )}
            <div><Label>Description {prePlan.classification !== "addon" && "(optional)"}</Label><Textarea data-testid="pre-plan-description" value={prePlan.description} onChange={(e) => setPrePlan({ ...prePlan, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount received</Label><Input data-testid="pre-plan-amount" type="number" value={prePlan.amount} onChange={(e) => setPrePlan({ ...prePlan, amount: e.target.value })} /></div>
              <div><Label>Payment date</Label><Input data-testid="pre-plan-date" type="date" value={prePlan.paid_at} onChange={(e) => setPrePlan({ ...prePlan, paid_at: e.target.value })} /></div>
              {prePlan.classification === "addon" && (<>
                <div><Label>GST</Label>
                  <Select value={prePlan.gst_included} onValueChange={(v) => setPrePlan({ ...prePlan, gst_included: v })}>
                    <SelectTrigger data-testid="pre-plan-gst-included"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">Included</SelectItem><SelectItem value="false">Excluded</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>GST rate</Label><Input data-testid="pre-plan-gst-rate" type="number" step="0.01" value={prePlan.gst_rate} onChange={(e) => setPrePlan({ ...prePlan, gst_rate: e.target.value })} /></div>
              </>)}
              <div><Label>Method</Label>
                <Select value={prePlan.method} onValueChange={(v) => setPrePlan({ ...prePlan, method: v })}>
                  <SelectTrigger data-testid="pre-plan-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["upi", "bank_transfer", "card", "cash", "cheque", "gateway"].map((x) => <SelectItem key={x} value={x}>{x.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Transaction reference</Label><Input data-testid="pre-plan-reference" value={prePlan.reference} onChange={(e) => setPrePlan({ ...prePlan, reference: e.target.value })} placeholder="UTR / txn id" /></div>
            </div>
            <div><Label>Notes</Label><Textarea data-testid="pre-plan-notes" value={prePlan.notes} onChange={(e) => setPrePlan({ ...prePlan, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="pre-plan-submit" onClick={recordPrePlanPay} disabled={!prePlan.amount}>Record payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjustOpen} onOpenChange={(o) => !o && setAdjustOpen(null)}>
        <DialogContent data-testid="registration-adjustment-dialog">
          <DialogHeader><DialogTitle>Reconcile {adjustOpen?.label} — {inr(adjustOpen?.amount_paid)} already received</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">How should this previously-collected payment be treated now that a Payment Plan exists? The original payment stays individually visible either way.</p>
            <div><Label>Treatment</Label>
              <Select value={adjust.treatment} onValueChange={(v) => setAdjust({ ...adjust, treatment: v })}>
                <SelectTrigger data-testid="adjust-treatment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjust_first_instalment">Adjust against a specific instalment</SelectItem>
                  <SelectItem value="adjust_total_package">Adjust against the total package fee</SelectItem>
                  <SelectItem value="separate">Keep as separate — do not adjust</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {adjust.treatment === "adjust_first_instalment" && (
              <div><Label>Which instalment?</Label>
                <Select value={adjust.target_obligation_key} onValueChange={(v) => setAdjust({ ...adjust, target_obligation_key: v })}>
                  <SelectTrigger data-testid="adjust-target-obligation"><SelectValue placeholder="Select instalment" /></SelectTrigger>
                  <SelectContent>
                    {(plans.find((s) => s.plan.plan_id === adjust.plan_id)?.obligations || []).map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.label} ({inr(o.expected_amount)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="adjust-submit" onClick={applyAdjustment}>Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent data-testid="new-plan-dialog">
          <DialogHeader><DialogTitle>New payment plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Opportunity</Label>
              <Select value={np.opportunity_id} onValueChange={(v) => setNp({ opportunity_id: v })}>
                <SelectTrigger data-testid="plan-opportunity"><SelectValue placeholder="Select opportunity" /></SelectTrigger>
                <SelectContent>{opportunities.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_code} · {o.opportunity_id.slice(0, 8)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {npOpp && !npInfo && npInfoError && <NoCommercialPlan productCode={npOpp.product_code} />}
            {npInfo && (
              <PlanInputsFields info={npInfo} inputs={npInputs} onInputsChange={setNpInputs}
                totalAgreed={npTotalAgreed} onTotalAgreedChange={setNpTotalAgreed}
                totalOverrideLabel="Total override (₹, optional)"
                discount={npDiscount} onDiscountChange={setNpDiscount} showDiscount
                testIdPrefix="plan" />
            )}
            <div className="text-xs text-muted-foreground">Instalments are calculated automatically from the active versioned Commercial Plan — no manual maths.</div>
          </div>
          <DialogFooter><Button data-testid="plan-create" onClick={createPlan} disabled={!np.opportunity_id || !npInfo}>Create plan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOpen} onOpenChange={(o) => !o && setEditOpen(null)}>
        <DialogContent data-testid="edit-plan-dialog">
          <DialogHeader><DialogTitle>Edit payment plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Obligations recalculate from the plan's original Commercial Plan version. Amounts already verified as paid are never reduced below what's been received.</div>
            {epInfo && (
              <PlanInputsFields info={epInfo} inputs={epInputs} onInputsChange={setEpInputs}
                totalAgreed={ep.total_agreed} onTotalAgreedChange={(v) => setEp({ ...ep, total_agreed: v })}
                totalOverrideLabel="Total override (₹, optional — raises the total only)"
                discount={ep.discount} onDiscountChange={(v) => setEp({ ...ep, discount: v })} showDiscount
                testIdPrefix="edit-plan" />
            )}
            {Object.keys(obligationEdits).length > 0 && (
              <div className="space-y-1.5">
                <Label>Instalment amounts (override individually if needed)</Label>
                {Object.entries(obligationEdits).map(([key, o]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-xs flex-1">{o.label}</span>
                    <Input data-testid={`edit-obligation-${key}`} type="number" className="w-32 h-8"
                      value={o.value} onChange={(e) => setObligationEdits({ ...obligationEdits, [key]: { ...o, value: e.target.value } })} />
                  </div>
                ))}
                {Object.entries(obligationEdits).some(([, o]) => Number(o.value) !== Number(o.original)) && (
                  <div className="space-y-1">
                    <Label className="text-xs">Reason for this correction (required)</Label>
                    <Textarea data-testid="edit-plan-reason" rows={2} value={editReason} onChange={(e) => setEditReason(e.target.value)} />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="edit-plan-save" onClick={saveEdit}>Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelOpen} onOpenChange={(o) => !o && setCancelOpen(null)}>
        <DialogContent data-testid="cancel-plan-dialog">
          <DialogHeader><DialogTitle>Cancel payment plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">The plan is flagged cancelled and stops accepting new payments. It stays visible with its full history — nothing is deleted.</div>
            <div><Label>Reason</Label><Textarea data-testid="cancel-plan-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="destructive" data-testid="cancel-plan-confirm" onClick={cancelPlan} disabled={!cancelReason.trim()}>Cancel plan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ApprovalRequests = ({ contactId, opportunities = [], canRequest, canDirectOwnerChange, canReallocatePayment, selectedOppId }) => {
  const [open, setOpen] = useState(null); // 'owner' | 'product' | 'changeProduct' | 'reallocate'
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState({ opportunity_id: "", new_owner: "", product: "", reason: "",
    source_payment_id: "", target_obligation_id: "", amount: "" });
  const [changePreview, setChangePreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [plans, setPlans] = useState([]);
  const [reallocPreview, setReallocPreview] = useState(null);
  const [reallocPreviewErr, setReallocPreviewErr] = useState("");
  useEffect(() => {
    if (open) {
      api.get("/users").then((r) => setUsers(r.data.users.filter((u) => u.is_active))).catch(() => {});
      api.get("/products", { params: { selectable_only: true } }).then((r) => setProducts(r.data)).catch(() => {});
    }
    if (open === "owner" || open === "changeProduct") {
      // Defaults to whichever opportunity is currently open on the page —
      // a deliberate choice, per 2026-09's Customer 360 control cleanup, so
      // a request never silently targets the wrong one; the picker below
      // still lets someone visibly choose a different opportunity.
      setSel((s) => ({ ...s, opportunity_id: selectedOppId || s.opportunity_id }));
    }
    if (open === "reallocate") {
      // Every plan this CUSTOMER holds, across every one of their
      // opportunities — a reallocation moves money between two of THEIR
      // OWN opportunities' plans, never a different customer's.
      api.get(`/finance/plans/by-contact/${contactId}`).then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Payment Reallocation preview — GST/reimbursement mismatch and the
  // source payment's real remaining available balance are always
  // computed server-side, never guessed at here.
  useEffect(() => {
    if (open !== "reallocate" || !sel.source_payment_id || !sel.target_obligation_id) {
      setReallocPreview(null); setReallocPreviewErr(""); return;
    }
    let cancelled = false;
    api.post("/finance/payment-reallocation/preview", {
      source_payment_id: sel.source_payment_id, target_obligation_id: sel.target_obligation_id,
    }).then((r) => { if (!cancelled) { setReallocPreview(r.data); setReallocPreviewErr(""); } })
      .catch((e) => { if (!cancelled) { setReallocPreview(null); setReallocPreviewErr(errDetail(e)); } });
    return () => { cancelled = true; };
  }, [open, sel.source_payment_id, sel.target_obligation_id]);
  const sourcePaymentOptions = plans.flatMap((s) => (s.payments || [])
    .filter((p) => p.verification_status === "verified" && !p.reversed)
    .map((p) => ({ ...p, product_code: s.plan.product_code })));
  const targetObligationOptions = plans.flatMap((s) => (s.obligations || [])
    .filter((o) => o.status !== "paid" && o.plan_id !== (sel.source_payment_id
      ? sourcePaymentOptions.find((p) => p.payment_id === sel.source_payment_id)?.plan_id : null))
    .map((o) => ({ ...o, product_code: s.plan.product_code })));
  // Change Product previews live (which of the two workflows applies is
  // decided server-side from the opportunity's own real state, e.g.
  // whether it already has a Process Case — never guessed at in the UI).
  useEffect(() => {
    if (open !== "changeProduct" || !sel.opportunity_id || !sel.product) {
      setChangePreview(null); setPreviewErr(""); return;
    }
    let cancelled = false;
    api.post(`/opportunities/${sel.opportunity_id}/product-change-preview`, { new_product_code: sel.product })
      .then((r) => { if (!cancelled) { setChangePreview(r.data); setPreviewErr(""); } })
      .catch((e) => { if (!cancelled) { setChangePreview(null); setPreviewErr(errDetail(e)); } });
    return () => { cancelled = true; };
  }, [open, sel.opportunity_id, sel.product]);
  const resetSel = () => setSel({ opportunity_id: "", new_owner: "", product: "", reason: "",
    source_payment_id: "", target_obligation_id: "", amount: "" });
  const submit = async () => {
    try {
      if (open === "changeProduct") {
        await api.post(`/opportunities/${sel.opportunity_id}/product-change-request`, {
          new_product_code: sel.product, reason: sel.reason,
        });
      } else if (open === "reallocate") {
        await api.post("/finance/payment-reallocation/request", {
          source_payment_id: sel.source_payment_id, target_obligation_id: sel.target_obligation_id,
          amount: Number(sel.amount), reason: sel.reason,
        });
      } else {
        await api.post("/approvals", {
          type: open === "owner" ? "ownership_change" : "product_addition",
          contact_id: contactId,
          opportunity_id: sel.opportunity_id || null,
          product_code: open === "product" ? sel.product : null,
          reason: sel.reason,
          payload: open === "owner" ? { new_owner_user_id: sel.new_owner } : { product_code: sel.product },
        });
      }
      toast.success("Approval request sent to Leadership");
      setOpen(null); resetSel(); setChangePreview(null); setReallocPreview(null);
    } catch (e) { toast.error(errDetail(e)); }
  };
  if (!canRequest && !canReallocatePayment) return null;
  const titles = { owner: "Ownership change", product: "Add another product",
    changeProduct: "Change product", reallocate: "Payment reallocation" };
  return (
    <>
      {/* flex-wrap (2026-09 R6 mobile pass): these approval actions used to
          sit on one unwrapped row, which pushed the whole Customer 360 page
          to ~593px wide on a 320–390px phone. Every action stays visible —
          they just wrap onto further rows when the width runs out. */}
      <div className="flex flex-wrap gap-2 min-w-0" data-testid="c360-approval-actions">
        {/* Users who genuinely hold crm.assign already have a real, direct
            "Assign" control right on this page (OpportunityLeadershipActions)
            — showing this approval-based one too would be a second control
            for the same mutation. Only rendered for people without that
            direct capability, and it's always honestly labelled "(approval)"
            now — canDirectOwnerChange is crm.assign-driven, not the dead
            owner.change capability, so this condition is never true at the
            same time the real Assign button is visible (2026-09 fix). */}
        {canRequest && !canDirectOwnerChange && (
          <Button size="sm" variant="outline" data-testid="request-owner-change"
            onClick={() => setOpen("owner")}>Change owner (approval)</Button>
        )}
        {canRequest && (
          <Button size="sm" variant="outline" data-testid="request-add-product" onClick={() => setOpen("product")}>Add product (approval)</Button>
        )}
        {canRequest && (
          <Button size="sm" variant="outline" data-testid="request-change-product" onClick={() => setOpen("changeProduct")}>Change product (approval)</Button>
        )}
        {/* Creatable by anyone holding finance.record — a proposal only;
            execution still requires a Leadership decider with
            approvals.manage via the standard Approvals decide flow. */}
        {canReallocatePayment && (
          <Button size="sm" variant="outline" data-testid="request-reallocate-payment"
            onClick={() => setOpen("reallocate")}>Reallocate payment (approval)</Button>
        )}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="sm:max-w-md" data-testid="approval-request-dialog">
          <DialogHeader><DialogTitle>{titles[open]} — approval request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(open === "owner" || open === "changeProduct") && (
              <div><Label className="text-xs">Opportunity</Label>
                <Select value={sel.opportunity_id} onValueChange={(v) => setSel({ ...sel, opportunity_id: v })}>
                  <SelectTrigger data-testid="req-opportunity"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{opportunities.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_code}{o.opportunity_id === selectedOppId ? " (currently open)" : ""}</SelectItem>)}</SelectContent>
                </Select>
                {sel.opportunity_id && sel.opportunity_id !== selectedOppId && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                    Targeting a different opportunity than the one currently open on this page.
                  </p>
                )}</div>
            )}
            {open === "owner" && (
              <div><Label className="text-xs">Proposed new owner</Label>
                <Select value={sel.new_owner} onValueChange={(v) => setSel({ ...sel, new_owner: v })}>
                  <SelectTrigger data-testid="req-new-owner"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            {open === "product" && (
              <div><Label className="text-xs">Product to add (creates a new Opportunity, never a duplicate Contact)</Label>
                <Select value={sel.product} onValueChange={(v) => setSel({ ...sel, product: v })}>
                  <SelectTrigger data-testid="req-product"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            {open === "changeProduct" && (<>
              <div><Label className="text-xs">New product</Label>
                <Select value={sel.product} onValueChange={(v) => setSel({ ...sel, product: v })}>
                  <SelectTrigger data-testid="req-change-product"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
              {previewErr && <p className="text-xs text-red-600" data-testid="change-product-preview-error">{previewErr}</p>}
              {changePreview && changePreview.mode === "discontinue_and_replace" && (
                <div className="rounded-md border border-amber-300 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-400"
                    data-testid="change-product-preview-note">
                  {changePreview.note}
                </div>
              )}
              {changePreview && changePreview.mode === "product_change" && changePreview.conflicts?.length > 0 && (
                <div className="rounded-md border p-2 text-xs space-y-1" data-testid="change-product-preview-conflicts">
                  {changePreview.conflicts.map((c) => <div key={c.type}>{c.message}</div>)}
                </div>
              )}
              {changePreview && changePreview.destination_selectable === false && (
                <p className="text-xs text-red-600">{changePreview.destination_reason}</p>
              )}
            </>)}
            {open === "reallocate" && (<>
              <div><Label className="text-xs">Source payment (already verified — never altered by this)</Label>
                <Select value={sel.source_payment_id} onValueChange={(v) => setSel({ ...sel, source_payment_id: v, target_obligation_id: "" })}>
                  <SelectTrigger data-testid="realloc-source-payment"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{sourcePaymentOptions.map((p) => (
                    <SelectItem key={p.payment_id} value={p.payment_id}>
                      {p.product_code} — ₹{p.amount} ({fmtIST(p.paid_at)})
                    </SelectItem>))}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Target obligation (on a different opportunity's plan)</Label>
                <Select value={sel.target_obligation_id} onValueChange={(v) => setSel({ ...sel, target_obligation_id: v })}>
                  <SelectTrigger data-testid="realloc-target-obligation"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{targetObligationOptions.map((o) => (
                    <SelectItem key={o.obligation_id} value={o.obligation_id}>
                      {o.product_code} — {o.label} (outstanding ₹{o.outstanding})
                    </SelectItem>))}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Amount to reallocate</Label>
                <Input type="number" min="0" step="0.01" data-testid="realloc-amount"
                  value={sel.amount} onChange={(e) => setSel({ ...sel, amount: e.target.value })} /></div>
              {reallocPreviewErr && <p className="text-xs text-red-600" data-testid="realloc-preview-error">{reallocPreviewErr}</p>}
              {reallocPreview && (
                <div className="rounded-md border p-2 text-xs space-y-1" data-testid="realloc-preview">
                  <div>Available to reallocate from this payment: ₹{reallocPreview.available_to_reallocate}</div>
                  {reallocPreview.gst_mismatch && (
                    <div className="text-amber-600 dark:text-amber-400 font-medium">
                      GST/reimbursement treatment differs between source ({reallocPreview.source_gst_rate}) and
                      target ({reallocPreview.target_gst_rate}) — Leadership will need to confirm the applicable
                      tax treatment explicitly when deciding this request.
                    </div>
                  )}
                </div>
              )}
            </>)}
            <div><Label className="text-xs">Reason</Label><Textarea data-testid="req-reason" value={sel.reason} onChange={(e) => setSel({ ...sel, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="req-submit" onClick={submit} disabled={
            open === "owner" ? (!sel.opportunity_id || !sel.new_owner) :
            open === "changeProduct" ? (!sel.opportunity_id || !sel.product || !sel.reason.trim()) :
            open === "reallocate" ? (!sel.source_payment_id || !sel.target_obligation_id
              || !sel.amount || Number(sel.amount) <= 0 || !sel.reason.trim()) :
            !sel.product}>Send for approval</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const NotesTab = ({ contactId, canAdd }) => {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [origin, setOrigin] = useState("sales");
  const load = useCallback(() => api.get(`/notes?contact_id=${contactId}`).then((r) => setNotes(r.data)).catch(() => setNotes([])), [contactId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post("/notes", { contact_id: contactId, text, origin }); toast.success("Note added"); setText(""); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const ORIGIN_COLORS = { sales: "bg-sky-500/15 text-sky-600", consultation: "bg-violet-500/15 text-violet-600", process: "bg-emerald-500/15 text-emerald-600", support: "bg-amber-500/15 text-amber-600", finance: "bg-rose-500/15 text-rose-600" };
  return (
    <div className="space-y-3" data-testid="c360-notes">
      {canAdd && (
        <div className="rounded-md border p-3 space-y-2">
          <Textarea data-testid="note-text" placeholder="Add a note — history is append-only with author and timestamp" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={origin} onValueChange={setOrigin}>
              <SelectTrigger className="w-40" data-testid="note-origin"><SelectValue /></SelectTrigger>
              <SelectContent>{["sales", "consultation", "process", "support", "finance"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" data-testid="note-add-btn" onClick={add} disabled={!text.trim()}><StickyNote className="h-4 w-4 mr-1" />Add note</Button>
          </div>
        </div>
      )}
      {notes === null ? <div className="text-sm text-muted-foreground">Loading…</div> :
        notes.length === 0 ? <EmptyState icon={StickyNote} title="No notes yet" hint="Notes from Sales, Consultation, Process, Support and Finance appear here." /> :
          notes.map((n) => (
            <div key={n.interaction_id} className="rounded-md border p-3 text-sm" data-testid={`note-${n.interaction_id}`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <span className="font-medium text-foreground">{n.actor_name || n.actor}</span>
                <Badge className={ORIGIN_COLORS[n.outcome] || "bg-muted"}>{n.outcome || "note"}</Badge>
                <span>{fmtIST(n.created_at)}</span>
              </div>
              <div className="whitespace-pre-wrap">{n.summary}</div>
            </div>
          ))}
    </div>
  );
};
